import {useMemo} from "react";
import {RootState, useAppSelector} from "../../../store";
import {IProfileWish, IWish} from "../../../store/wish/Helper";
import {computeSeededTicketsForWish} from "./TicketsCountLinearProgressHelper";

interface UseWishTicketAvailabilityParams {
    wish: IWish | IProfileWish
    accumulateTotals?: boolean
}

interface UseWishTicketAvailabilityResult {
    current: number
    total: number
    remaining: number
}

const useWishTicketAvailability = ({
                                       wish,
                                       accumulateTotals = false
                                   }: UseWishTicketAvailabilityParams): UseWishTicketAvailabilityResult => {
    const serverTime = useAppSelector((state: RootState) => state.appReducer.serverTime);

    const wishes = useMemo(() => [wish, ...(wish.children ?? [])], [wish]);

    const total = useMemo(
        () =>
            accumulateTotals
                ? wishes.reduce((accumulator, currentWish) => accumulator + currentWish.max_tickets, 0)
                : wish.max_tickets,
        [accumulateTotals, wish.max_tickets, wishes]
    );

    const current = useMemo(() => {
        const nowMs = serverTime ?? Date.now();
        const list = accumulateTotals ? wishes : [wish];

        return list.reduce(
            (accumulator, currentWish) =>
                accumulator +
                computeSeededTicketsForWish({
                    id: currentWish.id,
                    actual: currentWish.tickets_count,
                    max: currentWish.max_tickets,
                    startISO: currentWish.start_time,
                    endISO: currentWish.end_time,
                    nowMs,
                }),
            0
        );
    }, [accumulateTotals, wish, wishes, serverTime]);

    const remaining = useMemo(() => Math.max(total - current, 0), [current, total]);

    return {current, total, remaining};
}

export default useWishTicketAvailability;
